import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { searchPlaces, type PlaceSuggestion } from '../schedule/placeSearch';
import { Field, inputStyle } from './styles';

/**
 * A street-address box with suggestions from Photon (OpenStreetMap), debounced
 * and abandoned when the next keystroke starts another search. `HomePicker`
 * uses it for a home; the map's popover for a place to measure from.
 *
 * The text is the parent's (`query`), so it can clear the box when something
 * else is chosen; the suggestions are the box's own.
 */

const Input = styled('input')({
  ...inputStyle,
  '&::placeholder': { color: 'rgba(243,238,231,0.45)' },
});

const Suggestions = styled('ul')({
  listStyle: 'none',
  margin: '4px 0 0',
  padding: 4,
  maxHeight: 200,
  overflowY: 'auto',
  borderRadius: 10,
  background: BRAND.mochaDeep,
  border: '1px solid rgba(243,238,231,0.2)',
});

const Suggestion = styled('button')({
  appearance: 'none',
  display: 'block',
  width: '100%',
  textAlign: 'left',
  background: 'none',
  border: 'none',
  borderRadius: 6,
  padding: '6px 9px',
  cursor: 'pointer',
  color: BRAND.parchment,
  fontFamily: FONTS.body,
  fontSize: 13,
  fontWeight: 600,
  '& small': { display: 'block', fontSize: 11, fontWeight: 500, opacity: 0.65 },
  '&:hover, &:focus-visible': { background: 'rgba(243,238,231,0.12)', outline: 'none' },
});

const Attribution = styled('div')({
  marginTop: 4,
  fontFamily: FONTS.body,
  fontSize: 10,
  color: 'rgba(243,238,231,0.5)',
  '& a': { color: 'inherit' },
});

interface Props {
  id: string;
  label: string;
  query: string;
  onQuery: (query: string) => void;
  onChoose: (place: PlaceSuggestion) => void;
  placeholder?: string;
  autoFocus?: boolean;
}

export default function AddressSearch({ id, label, query, onQuery, onChoose, placeholder = 'Start typing a street address', autoFocus }: Props) {
  const [suggestions, setSuggestions] = useState<PlaceSuggestion[]>([]);
  /** Only typing searches: a chosen suggestion written back into the box doesn't. */
  const typed = useRef(false);
  const input = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (autoFocus) input.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    if (!typed.current) return;
    if (query.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      searchPlaces(query, controller.signal).then((found) => {
        if (!controller.signal.aborted) setSuggestions(found);
      });
    }, 300);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  // Cleared from outside (a neighborhood chosen instead): nothing left to suggest.
  if (!query && suggestions.length) setSuggestions([]);

  const choose = (s: PlaceSuggestion) => {
    typed.current = false;
    setSuggestions([]);
    onChoose(s);
  };

  return (
    <Field>
      <label htmlFor={id}>{label}</label>
      <Input
        id={id}
        ref={input}
        type="text"
        value={query}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          typed.current = true;
          onQuery(e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && suggestions[0]) {
            e.preventDefault();
            choose(suggestions[0]);
          }
        }}
      />
      {suggestions.length > 0 && (
        <Suggestions>
          {suggestions.map((s) => (
            <li key={`${s.label}|${s.detail}`}>
              <Suggestion type="button" onClick={() => choose(s)}>
                {s.label}
                {s.detail && <small>{s.detail}</small>}
              </Suggestion>
            </li>
          ))}
        </Suggestions>
      )}
      <Attribution>
        Address search by <a href="https://photon.komoot.io" target="_blank" rel="noreferrer">Photon</a>, ©{' '}
        <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a>{' '}
        contributors. Your address stays in this browser.
      </Attribution>
    </Field>
  );
}
